import {
  ApiCallState,
  callFailed,
  callInitiated,
  callSucceeded
} from '../../types'
import { toggleElement } from '../helpers'
import { WatchList } from '../watchList/types'
import { ListTradingAction } from './actions'
import { objectsAreEqual } from '../../helpers/utils'
import { IdentifierResolutions, securityToOrder, updateArray } from './helpers'
import { ListTradingSecurity, WorkingListTradingOrder } from './types'
import { Order } from '../order/types'

type WorkingSecurityHash = Record<
  ListTradingSecurity['id'],
  WorkingListTradingOrder
>

type SpecialWatchlistId = 'new' | 'invalidWatchlist'

export interface State {
  securitiesList: ListTradingSecurity[]
  // fetch call state
  pending: boolean
  error: boolean
  newWatchlistTransactionId: number
  createListState?: ApiCallState
  identifierResolutions: IdentifierResolutions
  workingSecurities: WorkingSecurityHash
  watchlistId: WatchList['id'] | SpecialWatchlistId
  identifierValidationStatus?: 'pending' | 'valid' | 'invalid'
  // live data for accumulated orders watched via GetOrderById, keyed by order id
  watchedOrders: Record<string, Order>
  // order ids with an active watch stream (subscription membership)
  watchedOrderIds: string[]
}

const workingSecurities: WorkingSecurityHash = {}

export const initialState: State = {
  securitiesList: [],
  pending: true,
  error: false,
  newWatchlistTransactionId: 0,
  identifierResolutions: {},
  workingSecurities,
  watchlistId: undefined,
  watchedOrders: {},
  watchedOrderIds: []
}

const addWorkingSecurities = (
  prev: WorkingSecurityHash,
  updates: ListTradingSecurity[],
  isTemp: boolean
) => {
  let changed = false
  const result = { ...prev }
  for (const update of updates) {
    const prevWorkingSecurity = prev[update.id]
    const prevSelectedOrders = prevWorkingSecurity?.selectedOrders ?? []
    const ordersToCancel = prevWorkingSecurity?.ordersToCancel ?? []
    /*
       As depth shifts, accumulate the new top-of-stack into an already-checked
       security's selection instead of replacing it — unbounded, deduped, and
       newest first. Trade allocation doesn't rely on this order:
       tradeSelectedSecurities sorts by live price.
    */
    const newBestOrderId = update.bestOrder?.id
    const selectedOrders =
      prevSelectedOrders.length > 0 &&
      newBestOrderId &&
      !prevSelectedOrders.includes(newBestOrderId)
        ? [newBestOrderId, ...prevSelectedOrders]
        : prevSelectedOrders
    const workingSecurity = {
      ...securityToOrder(update, update.id, isTemp),
      ordersToCancel,
      selectedOrders
    }
    /*
       Reuse the prior ref when nothing changed so market ticks don't churn
       selectors/editors (mirrors updateArray for securitiesList).
    */
    if (
      prevWorkingSecurity &&
      objectsAreEqual(prevWorkingSecurity, workingSecurity)
    ) {
      continue
    }
    result[update.id] = workingSecurity
    changed = true
  }
  return changed ? result : prev
}

/*
   Seed a just-selected/accumulated order's data into watchedOrders so a submit
   that races its GetOrderById watch still has price/size; the watch then keeps
   it fresh and prunes it. Never clobbers data the watch already stored.
*/
const seedWatchedOrder = (
  watched: Record<string, Order>,
  order: Order | undefined
): Record<string, Order> =>
  order && !(order.id in watched) ? { ...watched, [order.id]: order } : watched

const updateWorkingOrders = (
  prev: WorkingSecurityHash,
  updates: WorkingListTradingOrder[],
  isTemp: boolean = false
) => {
  const result = { ...prev }
  for (const update of updates) {
    result[update.securityId] = {
      ...update,
      selectedOrders: update.selectedOrders ?? [],
      isTemp
    }
  }
  return result
}

export const reducer = (
  state = initialState,
  action: ListTradingAction
): State => {
  switch (action.type) {
    case 'listTrading.createList':
      return { ...state, createListState: callInitiated }
    case 'listTrading.createListFailure':
      return {
        ...state,
        createListState: { ...callFailed, status: action.payload.message }
      }

    case 'listTrading.validateIdentifiers':
      return { ...state, identifierValidationStatus: 'pending' as const }

    case 'listTrading.validateIdentifiersComplete':
      return {
        ...state,
        identifierValidationStatus: action.payload.valid
          ? ('valid' as const)
          : ('invalid' as const),
        identifierResolutions: action.payload.resolutions
      }

    case 'listTrading.validateIdentifiersFailed':
      return { ...state, identifierValidationStatus: undefined }

    case 'listTrading.clearCreateListTradingListError':
      const { createListState, ...rest } = state
      return {
        ...rest,
        identifierResolutions: {},
        identifierValidationStatus: undefined
      }

    case 'listTrading.fetch':
      return {
        ...state,
        pending: true,
        error: false,
        securitiesList: [],
        workingSecurities
      }

    case 'listTrading.fetchFail':
      return {
        ...state,
        pending: false,
        error: true
      }

    case 'listTrading.fetchSuccess': {
      const nextWorkingSecurities = addWorkingSecurities(
        state.workingSecurities,
        action.payload,
        false
      )
      let watchedOrders = state.watchedOrders
      for (const update of action.payload) {
        const best = update.bestOrder
        if (
          best &&
          nextWorkingSecurities[update.id]?.selectedOrders.includes(best.id)
        ) {
          watchedOrders = seedWatchedOrder(watchedOrders, best)
        }
      }
      return {
        ...state,
        securitiesList: updateArray(state.securitiesList, action.payload),
        workingSecurities: nextWorkingSecurities,
        watchedOrders,
        ...callSucceeded
      }
    }

    case 'listTrading.createWorkingListTradingOrder':
      // watchlist id used in epic, discard and just use the working order
      const { watchlistId, ...order } = action.payload
      const prevOrder = state.workingSecurities[order.securityId]
      const isTemp = prevOrder?.isTemp ?? true
      return {
        ...state,
        workingSecurities: {
          ...state.workingSecurities,
          [action.payload.securityId]: { ...order, isTemp }
        }
      }

    case 'listTrading.selectListTradingSecurities': {
      const newWorkingSecurities: WorkingSecurityHash = {}
      const securityIds = Object.keys(state.workingSecurities)
      let watchedOrders = state.watchedOrders
      securityIds.forEach((securityId) => {
        const workingSecurity = {
          ...state.workingSecurities[Number(securityId)]
        }
        if (action.payload.includes(Number(securityId))) {
          if (!workingSecurity.selectedOrders.length) {
            const matchingSecurity = state.securitiesList.find(
              (security) => security.id === Number(securityId)
            )
            if (matchingSecurity) {
              const bestTrade = matchingSecurity.bestOrder
              workingSecurity.selectedOrders = bestTrade ? [bestTrade.id] : []
              watchedOrders = seedWatchedOrder(watchedOrders, bestTrade)
            }
          }
        } else {
          workingSecurity.selectedOrders = []
        }
        newWorkingSecurities[Number(securityId)] = workingSecurity
      })
      return {
        ...state,
        workingSecurities: newWorkingSecurities,
        watchedOrders
      }
    }

    case 'listTrading.toggleSuborderSelection': {
      const ws = state.workingSecurities[action.payload.securityId]
      return {
        ...state,
        workingSecurities: {
          ...state.workingSecurities,
          [action.payload.securityId]: {
            ...ws,
            selectedOrders: toggleElement(
              ws.selectedOrders,
              action.payload.orderId
            )
          }
        }
      }
    }

    case 'listTrading.toggleCancelSubOrderSelection': {
      const ws = state.workingSecurities[action.payload.securityId]
      return {
        ...state,
        workingSecurities: {
          ...state.workingSecurities,
          [action.payload.securityId]: {
            ...ws,
            ordersToCancel: toggleElement(
              ws.ordersToCancel,
              action.payload.orderId
            )
          }
        }
      }
    }

    case 'listTrading.tradeSelectedSecurities': {
      return {
        ...state,
        securitiesList: updateArray(
          state.securitiesList,
          state.securitiesList.map((s) => ({ ...s, error: '' }))
        )
      }
    }

    case 'listTrading.setListTradeErrors': {
      return {
        ...state,
        securitiesList: updateArray(state.securitiesList, action.payload)
      }
    }

    /*
        Note the reason there is no action to select and deselect
        all orders on all security ids is because the pending orders
        live in store/order, not here.
     */
    case 'listTrading.selectSecurityOrdersToCancel': {
      const ws = state.workingSecurities[action.payload.securityId]
      return {
        ...state,
        workingSecurities: {
          ...state.workingSecurities,
          [action.payload.securityId]: {
            ...ws,
            ordersToCancel: action.payload.orderIds
          }
        }
      }
    }

    case 'listTrading.deselectSecurityOrdersToCancel': {
      const ws = state.workingSecurities[action.payload.securityId]
      if (!ws) {
        return state
      }
      return {
        ...state,
        workingSecurities: {
          ...state.workingSecurities,
          [action.payload.securityId]: {
            ...ws,
            ordersToCancel: []
          }
        }
      }
    }

    case 'listTrading.setListTradingWatchlistId': {
      return { ...state, watchlistId: action.payload }
    }

    case 'listTrading.updateListSuccess':
      return {
        ...state,
        workingSecurities: updateWorkingOrders(
          state.workingSecurities,
          action.payload,
          false
        )
      }

    case 'listTrading.setListTradingTransactionId':
      return {
        ...state,
        newWatchlistTransactionId: action.payload
      }

    case 'listTrading.resetListTradingTransactionId':
      return {
        ...state,
        newWatchlistTransactionId: 0
      }

    case 'listTrading.watchOrder': {
      const { orderId } = action.payload
      if (state.watchedOrderIds.includes(orderId)) {
        return state
      }
      return {
        ...state,
        watchedOrderIds: [...state.watchedOrderIds, orderId]
      }
    }

    case 'listTrading.unwatchOrder': {
      const { orderId } = action.payload
      const { [orderId]: _removed, ...watchedOrders } = state.watchedOrders
      return {
        ...state,
        watchedOrderIds: state.watchedOrderIds.filter((id) => id !== orderId),
        watchedOrders
      }
    }

    case 'listTrading.setWatchedOrder': {
      const watchedOrder = action.payload
      return {
        ...state,
        watchedOrders: {
          ...state.watchedOrders,
          [watchedOrder.id]: watchedOrder
        }
      }
    }

    case 'listTrading.removeCancelledOrder': {
      const { securityId, orderId } = action.payload
      const ws = state.workingSecurities[securityId]
      if (!ws) {
        return state
      }
      return {
        ...state,
        workingSecurities: {
          ...state.workingSecurities,
          [securityId]: {
            ...ws,
            selectedOrders: ws.selectedOrders.filter((id) => id !== orderId)
          }
        }
      }
    }
    default:
      return state
  }
}
